import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  SectionHeader,
} from "../../components/design-system/index.js";
import { Toast, useToast } from "../../components/toast.js";
import {
  getLetterOfIntent,
  saveLetterOfIntent,
} from "../../lib/api-modules/estate.js";

const MAX_BYTES = 50 * 1024;

/**
 * /estate/letter-of-intent — Phase 9.
 *
 * Owner-only encrypted long-form note. Decryption + write happen
 * server-side via the EstateFn dedicated routes; the body never
 * touches the browser unless the owner is reading it. Markdown is the
 * input format (no rich-text editor in v1 — keep dependency surface
 * minimal).
 *
 * 50KB plaintext cap matches the server-side schema lock.
 */
export function EstateLetterOfIntentPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const loiQ = useQuery({
    queryKey: ["estate-letter-of-intent"],
    queryFn: getLetterOfIntent,
  });
  const [draft, setDraft] = useState<string | null>(null);

  // When the server payload arrives, seed the editor only if the user
  // hasn't started typing.
  useEffect(() => {
    if (draft === null && loiQ.data) {
      setDraft(loiQ.data.body ?? "");
    }
  }, [draft, loiQ.data]);

  const saveMut = useMutation({
    mutationFn: (body: string) => saveLetterOfIntent(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["estate-letter-of-intent"],
      });
      toast.show("Saved");
    },
    onError: (err) =>
      toast.show(err instanceof Error ? err.message : "Save failed"),
  });

  const value = draft ?? "";
  const byteSize = new Blob([value]).size;
  const overLimit = byteSize > MAX_BYTES;
  const isOwnerForbidden =
    loiQ.error instanceof Error && /ROLE_DENIED/.test(loiQ.error.message);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <Link
        to="/estate"
        className="inline-flex items-center text-sm text-[var(--color-accent)]"
      >
        ← Back to estate
      </Link>
      <SectionHeader
        title="Letter of intent"
        sub="A free-form note for whoever survives you. Encrypted at rest with your tenant key. Only owners can read."
      />
      <Toast message={toast.message} />

      {isOwnerForbidden ? (
        <Card>
          <p className="text-sm text-[var(--color-text)]">
            Only the household owner can read or edit the letter of intent.
          </p>
        </Card>
      ) : (
        <>
          <Card className="bg-[var(--color-warning-soft)]/40 text-sm text-[var(--color-warning)]">
            Encrypted at rest with your tenant key (envelope encryption,
            tenant-DEK + KMS). Hearth cannot read this without your owner-role
            access. Markdown is supported; rich-text comes later.
          </Card>

          <Card>
            <textarea
              value={value}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Dear daughter,&#10;&#10;If you're reading this..."
              className="block h-[40vh] w-full resize-y rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] p-3 font-mono text-sm text-[var(--color-text)] focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-[var(--color-muted)]">
                {byteSize.toLocaleString()} of {MAX_BYTES.toLocaleString()}{" "}
                bytes
                {overLimit && (
                  <span className="ml-2 text-[var(--color-danger)]">
                    (over limit)
                  </span>
                )}
                {loiQ.data?.updated_at && (
                  <>
                    {" · "}Last saved {loiQ.data.updated_at.slice(0, 16).replace(
                      "T",
                      " ",
                    )}
                  </>
                )}
              </p>
              <Button
                size="sm"
                onClick={() => saveMut.mutate(value)}
                disabled={
                  saveMut.isPending ||
                  loiQ.isLoading ||
                  overLimit ||
                  value === (loiQ.data?.body ?? "")
                }
              >
                {saveMut.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
